import { ID, Response } from 'app/components/Table/core/types';

import { TimeFrame } from 'app/modules/projection/core/type';
import { Ward } from 'app/modules/system-settings/locations/wards/core/type';

import { FormDate } from 'app/helpers';

import { Bin } from '../../bins/core/type';
import { Item } from '../../items/core/type';
import { Rack } from '../../racks/core/type';
import { StatusStockUp, StockLevel, StockRunType } from './enum';

export type StockRun = {
  id: ID;
  startTime: string;
  endTime: string;
  actualEndTime?: string;
  actualStartTime?: string;
  index: string;
  wardId: number;
  status: StatusStockUp;
  stockLevel: StockLevel;
  createdAt: string;
  updatedAt: string;
  location?: Ward;
  noOfBins: number;
  noOfBinsCompeted: number;
  noOfRacks: number;
  noOfRacksCompeted: number;
  addedBy?: {
    firstName: string;
    lastName: string;
  };
  updatedBy?: {
    firstName: string;
    lastName: string;
  };
  activate: number;
  createdBy?: string;
  roleName?: string;
  demand?: TimeFrame;
  demandProjections?: StockRunItem[];
  type?: StockRunType;
  forceCompletedReason?: string;
};

export interface InventoryStockValue {
  qtyBalance: number;
  restockValue: number;
  alarmValue: number;
  parValue: number;
}

export interface StockRunItem extends InventoryStockValue {
  id?: ID;
  uid?: string;
  bin?: Bin;
  inventory?: Item | null;
  rack?: Rack;
  quantity: string;
  qtyCompleted?: number;
  isCompleted?: boolean;
  order: number;
  recommendedQty?: number;
  lotNumber?: string;
  batchExpiryDate?: Date;
  expiry?: {
    lotNumber?: string;
    expiryDate?: Date;
    alertDate?: Date;
  };
  qtyUnit?: {
    id: ID;
    name: string;
    value: string;
  };
  forceCompletedReason?: string;
}

export type StockRunsQueryResponse = Response<{
  items: Array<StockRun>;
  total: number;
}>;
export type StockRunItemsQueryResponse = Response<{
  items: Array<StockRunItem>;
  total: number;
}>;
export type StockRunHistoryQueryResponse = Response<{
  items: Array<StockRunHistory>;
  total: number;
}>;

export type StockRunRequest = {
  startTime?: string;
  endTime: string;
  index?: string;
  wardId: number;
  status: number;
  type?: StockRunType;
  demandId?: ID;
};

export type StockRunItemRequest = {
  id?: ID;
  itemId: number;
  binId: number;
  quantity: number;
  stockRunId: number;
  order: number;
  recommendedQty?: number;
  batchExpiryDate?: Date;
  lotNumber?: string;
};

export const initialStockRun: StockRun = {
  id: '',
  endTime: '',
  index: '',
  startTime: '',
  status: StatusStockUp.Draft,
  stockLevel: StockLevel.normal,
  createdAt: '',
  updatedAt: '',
  wardId: 0,
  noOfBins: 0,
  noOfBinsCompeted: 0,
  noOfRacks: 0,
  noOfRacksCompeted: 0,
  activate: 0,
};

export interface HeaderTableItem {
  title: string;
  field: string;
  style?: React.CSSProperties;
  isRequired?: boolean;
}

export interface StockRunFilterDetail {
  status: string[];
  wardIds: string[];
  from_commence_date: string | null;
  to_commence_date: string | null;
  from_complete_date: string | null;
  to_complete_date: string | null;
  hideDateFilter?: boolean;

  c_from_commence_date?: string | null; // for calendar view query
  c_to_commence_date?: string | null; // for calendar view query
}

export interface StockRunHistory {
  id: ID;
  inventory?: Item;
  expriryDate: string;
  type: string;
  quantity: number;
  quantityUnit: string;
  weight: number;
  weightUnit: string;
  qtyToStockUp: number;
  qtyCompleted: number;
  episode: {
    user: {
      firstName: string;
      id: string;
      lastName: string;
    };
  };
  userName: string;
  roleName: string;
}

export interface ForecastItem {
  itemId: number;
  sku?: string;
  recommendedQty?: number;
}

export enum GiRequestStatus {
  Pending = 'pending',
}

export interface ReplenishmentCheckItem {
  id: ID;
  name: string;
  sku: string;
  unit: string;
  outstandingQty: number;
  smartwmsAvailableQty: number;
  stdPrice: number;
  shortfallQty: number;
  stockRunIds: ID[];
  giStatus: GiRequestStatus | null;
}

export type ReplenishmentCheckResponse = Response<{
  items: Array<ReplenishmentCheckItem>;
}>;
